import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EXTENDED_FIELDS, SECTIONS } from '@/data/extendedProfileFields';

const inputCls = 't-bg-input t-border h-9 rounded-lg text-sm px-3 t-text';

function FieldRow({ field, value, onChange, lang }) {
  const label = lang === 'en' ? field.label_en : field.label_id;
  const hint = lang === 'en' ? field.hint_en : field.hint_id;
  const testId = `ext-${field.key}`;

  if (field.type === 'yesno') {
    return (
      <div className="flex items-center justify-between gap-2 py-1.5">
        <div className="min-w-0 flex-1">
          <label className="text-xs font-medium t-text block">{label}</label>
          {hint && <p className="text-[10px] t-text-muted">{hint}</p>}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {[{ v: true, l: lang === 'en' ? 'Yes' : 'Ya' }, { v: false, l: lang === 'en' ? 'No' : 'Tidak' }, { v: null, l: '—' }].map(opt => (
            <button
              key={String(opt.v)}
              type="button"
              onClick={() => onChange(opt.v)}
              data-testid={`${testId}-${opt.v === null ? 'clear' : opt.v ? 'yes' : 'no'}`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                value === opt.v
                  ? (opt.v === true ? 'bg-emerald-500 text-white' : opt.v === false ? 'bg-slate-400 text-white' : 'bg-slate-200 t-text')
                  : 't-bg-input t-border t-text-muted hover:opacity-80'
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === 'slider') {
    const v = typeof value === 'number' ? value : field.min;
    return (
      <div className="py-1.5">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium t-text">{label}</label>
          <span className="text-sm font-bold t-text tabular-nums">
            {typeof value === 'number' ? `${v}/10` : '—'}
          </span>
        </div>
        <input
          type="range"
          min={field.min}
          max={field.max}
          value={v}
          onChange={e => onChange(Number(e.target.value))}
          data-testid={testId}
          className="w-full accent-[#D98A94]"
        />
        <div className="flex items-center gap-2 mt-1">
          <button type="button" onClick={() => onChange(null)} className="text-[10px] t-text-muted hover:underline">
            {lang === 'en' ? 'Clear' : 'Kosongkan'}
          </button>
        </div>
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div className="py-1.5">
        <label className="text-xs font-medium t-text block mb-1">{label}</label>
        <Select value={value || ''} onValueChange={v => onChange(v || null)}>
          <SelectTrigger data-testid={testId} className={inputCls + ' w-full'}>
            <SelectValue placeholder={lang === 'en' ? '-- Choose --' : '-- Pilih --'} />
          </SelectTrigger>
          <SelectContent>
            {field.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div className="py-1.5">
        <label className="text-xs font-medium t-text block mb-1">{label}</label>
        <Textarea
          value={value || ''}
          onChange={e => onChange(e.target.value || null)}
          maxLength={field.maxLength}
          data-testid={testId}
          className={inputCls + ' min-h-[60px] py-2'}
          placeholder={hint}
        />
      </div>
    );
  }

  if (field.type === 'number') {
    return (
      <div className="py-1.5">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium t-text">{label}{field.unit ? ` (${field.unit})` : ''}</label>
        </div>
        <Input
          type="number"
          min={field.min}
          max={field.max}
          value={value ?? ''}
          onChange={e => {
            const raw = e.target.value;
            if (raw === '') return onChange(null);
            const n = Number(raw);
            if (!Number.isNaN(n)) onChange(n);
          }}
          data-testid={testId}
          className={inputCls}
        />
        {hint && <p className="text-[10px] t-text-muted mt-1">{hint}</p>}
      </div>
    );
  }

  // text
  return (
    <div className="py-1.5">
      <label className="text-xs font-medium t-text block mb-1">{label}</label>
      <Input
        value={value || ''}
        onChange={e => onChange(e.target.value || null)}
        maxLength={field.maxLength}
        data-testid={testId}
        className={inputCls}
        placeholder={hint}
      />
    </div>
  );
}

/**
 * Reusable editor for the 24 optional profile fields. Renders 5 collapsible
 * sections. `value` is a flat object like { height_cm: 170, smokes: false, ... }.
 * `onFieldChange(key, value)` is called on each change (value can be null to clear).
 */
export function ExtendedProfileEditor({ value = {}, onFieldChange, lang = 'id', defaultOpen = false }) {
  const [openSections, setOpenSections] = useState(() => new Set(defaultOpen ? SECTIONS.map(s => s.key) : []));
  const toggle = (k) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  return (
    <div className="space-y-2" data-testid="extended-profile-editor">
      {SECTIONS.map(sec => {
        const open = openSections.has(sec.key);
        const fields = EXTENDED_FIELDS.filter(f => f.section === sec.key);
        const filledCount = fields.filter(f => value[f.key] !== undefined && value[f.key] !== null && value[f.key] !== '').length;
        return (
          <div key={sec.key} className="border t-border rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => toggle(sec.key)}
              data-testid={`ext-section-${sec.key}`}
              className="w-full flex items-center justify-between px-4 py-2.5 t-bg-input hover:opacity-90 transition-opacity text-left"
            >
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold t-text">{lang === 'en' ? sec.label_en : sec.label_id}</span>
                {filledCount > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#D98A94]/15 text-[#D98A94]">
                    {filledCount}/{fields.length}
                  </span>
                )}
              </span>
              <ChevronDown className={`w-4 h-4 t-text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
              <div className="px-4 py-3 border-t t-border grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                {fields.map(field => (
                  <FieldRow
                    key={field.key}
                    field={field}
                    value={value[field.key]}
                    onChange={v => onFieldChange(field.key, v)}
                    lang={lang}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
